import { css } from "@emotion/css";
import { Button, Callout, DeprecatedIconButton, LegacyDataTable, LegacyDataTableHeader, LegacyDataTableRow, LegacyDataTableHeaderColumn, LegacyDataTableBody, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import { space, fontSize } from "@octopusdeploy/design-system-tokens";
import type { ProjectResource, ReleaseResource, GetReleaseDeploymentBff, GetReleaseDetailsBffDeploymentTask, EnvironmentV2Resource } from "@octopusdeploy/octopus-server-client";
import { TaskState, Permission } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { groupBy } from "lodash";
import * as React from "react";
import InternalLink from "~/components/Navigation/InternalLink";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import DateFormatter from "~/utils/DateFormatter";
import DeploymentDetailsIcon from "../LifecycleProgression/DeploymentDetailsIcon";
const getActionButtonForEphemeralEnvironmentDeployments = (project: ProjectResource, release: ReleaseResource, items: EphemeralEnvironmentDeploymentTableData[]) => {
    if (deploymentStateIs(TaskState.Success, items[0].deployment.Id, items.map((item) => item.deploymentTask))) {
        return renderEnvironmentScopedDeploymentAction(project, release, "Redeploy...", [items[0].environment.Id], items[0].environment.Name);
    }
    else if (deploymentStateIs(TaskState.Failed, items[0].deployment.Id, items.map((item) => item.deploymentTask)) ||
        deploymentStateIs(TaskState.Canceled, items[0].deployment.Id, items.map((item) => item.deploymentTask))) {
        return renderRetryAction(project, release, "Try again...", items[0].deployment.Id, items[0].environment.Name);
    }
    return null;
};
const renderEnvironmentScopedDeploymentAction = (project: ProjectResource, release: ReleaseResource, label: string, environmentIds: string[], accessibleName: string | undefined) => {
    const url = links.deployReleasePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, releaseVersion: release.Version }, { environmentIds });
    return renderGenericDeploymentAction(label, url, accessibleName, environmentIds);
};
const renderGenericDeploymentAction = (label: string, url: LinkHref, accessibleName: string | undefined, environmentIds?: string[]) => {
    const hasPermissionToCreateDeploymentsInAllEnvironments = environmentIds
        ? environmentIds.every((e) => isAllowed({
            permission: Permission.DeploymentCreate,
            project: "*",
            environment: e,
            projectGroup: "*",
            tenant: "*",
        }))
        : true;
    if (hasPermissionToCreateDeploymentsInAllEnvironments) {
        return <Button importance="secondary" href={url} label={label} accessibleName={accessibleName}/>;
    }
    return <></>;
};
const renderRetryAction = (project: ProjectResource, release: ReleaseResource, label: string, previousDeploymentId: string, accessibleName: string | undefined) => {
    const url = links.deployReleasePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, releaseVersion: release.Version }, { previousDeploymentId });
    return renderGenericDeploymentAction(label, url, accessibleName);
};
const deploymentStateIs = (state: TaskState, deploymentId: string, deploymentTasks: GetReleaseDetailsBffDeploymentTask[]) => {
    const task = deploymentTasks.filter((deploymentTask) => deploymentTask.DeploymentId === deploymentId)[0];
    return task.State === state;
};
type EphemeralEnvironmentDeploymentTableData = {
    deployment: GetReleaseDeploymentBff;
    deploymentTask: GetReleaseDetailsBffDeploymentTask;
    environment: EnvironmentV2Resource;
};
type EphemeralEnvironmentDeploymentRowProps = {
    item: EphemeralEnvironmentDeploymentTableData;
    hasChildRows: boolean;
    showingChildRows: boolean;
    setShowChildRows: (show: boolean) => void;
    actionButton?: React.ReactNode;
};
function EphemeralEnvironmentDeploymentRow({ item, hasChildRows, showingChildRows, setShowChildRows, actionButton }: EphemeralEnvironmentDeploymentRowProps) {
    const taskTime = item.deploymentTask.CompletedTime ?? item.deploymentTask.StartTime;
    return (<LegacyDataTableRow key={item.deployment.Id}>
            <LegacyDataTableRowColumn>{item.environment.Name}</LegacyDataTableRowColumn>
            <LegacyDataTableRowColumn>
                <InternalLink to={links.taskPage.generateUrl({ taskId: item.deployment.TaskId })} accessibleName={`View deployment task for ${item.deployment.Name} queued for or started at ${taskTime}`}>
                    <DeploymentDetailsIcon task={item.deploymentTask} deployment={item.deployment} stripTopBottomPadding={true}/>
                </InternalLink>
            </LegacyDataTableRowColumn>
            <LegacyDataTableRowColumn>
                <span>{DateFormatter.dateToLongFormat(taskTime)}</span>
                {item.deploymentTask.Duration && <div className={styles.deploymentDuration}>Duration: {item.deploymentTask.Duration}</div>}
            </LegacyDataTableRowColumn>
            <LegacyDataTableRowColumn className={styles.deploymentRowActions}>
                {actionButton}
                <div className={styles.deploymentExpander}>{hasChildRows && <DeprecatedIconButton icon={showingChildRows ? "ChevronUpIcon" : "ChevronDownIcon"} onClick={() => setShowChildRows(!showingChildRows)}/>}</div>
            </LegacyDataTableRowColumn>
        </LegacyDataTableRow>);
}
type EphemeralEnvironmentDeploymentChildRowProps = {
    item: EphemeralEnvironmentDeploymentTableData;
};
function EphemeralEnvironmentDeploymentChildRow({ item }: EphemeralEnvironmentDeploymentChildRowProps) {
    const taskTime = item.deploymentTask.CompletedTime ?? item.deploymentTask.StartTime;
    return (<LegacyDataTableRow key={item.deployment.Id}>
            <LegacyDataTableRowColumn />
            <LegacyDataTableRowColumn>
                <InternalLink to={links.taskPage.generateUrl({ taskId: item.deployment.TaskId })} accessibleName={`View deployment task for ${item.deployment.Name} queued for or started at ${taskTime}`}>
                    <DeploymentDetailsIcon task={item.deploymentTask} deployment={item.deployment} stripTopBottomPadding={true}/>
                </InternalLink>
            </LegacyDataTableRowColumn>
            <LegacyDataTableRowColumn>
                <span>{DateFormatter.dateToLongFormat(taskTime)}</span>
                {item.deploymentTask.Duration && <div className={styles.deploymentDuration}>Duration: {item.deploymentTask.Duration}</div>}
            </LegacyDataTableRowColumn>
            <LegacyDataTableRowColumn />
        </LegacyDataTableRow>);
}
const styles = {
    deploymentRowActions: css({
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: space[16],
    }),
    deploymentExpander: css({
        display: "flex",
        justifyContent: "flex-end",
        minWidth: space[40],
    }),
    deploymentDuration: css({
        fontSize: fontSize.small,
    }),
};
type EphemeralEnvironmentDeploymentsProps = {
    project: ProjectResource;
    release: ReleaseResource;
    items: EphemeralEnvironmentDeploymentTableData[];
};
function EphemeralEnvironmentDeploymentsRow({ project, release, items }: EphemeralEnvironmentDeploymentsProps) {
    const [showChildRows, setShowChildRows] = React.useState(false);
    const actionButton = getActionButtonForEphemeralEnvironmentDeployments(project, release, items);
    return (<>
            {items.map((item, index) => index === 0 ? (<EphemeralEnvironmentDeploymentRow key={item.deployment.Id} item={item} hasChildRows={items.length > 1} showingChildRows={showChildRows} setShowChildRows={setShowChildRows} actionButton={actionButton}/>) : (showChildRows && <EphemeralEnvironmentDeploymentChildRow key={item.deployment.Id} item={item}/>))}
        </>);
}
type EphemeralEnvironmentDeploymentsTableProps = {
    project: ProjectResource;
    release: ReleaseResource;
    deployments: GetReleaseDeploymentBff[];
    deploymentTasks: GetReleaseDetailsBffDeploymentTask[];
    environments: EnvironmentV2Resource[];
};
export function EphemeralEnvironmentDeploymentsTable({ project, release, deployments, deploymentTasks, environments }: EphemeralEnvironmentDeploymentsTableProps) {
    const data: EphemeralEnvironmentDeploymentTableData[] = [];
    for (const deployment of deployments) {
        const deploymentTask = deploymentTasks.find((task) => task.DeploymentId === deployment.Id);
        const environment = environments.find((env) => env.Id === deployment.EnvironmentId);
        if (!deploymentTask || !environment) {
            continue;
        }
        data.push({
            deployment: deployment,
            deploymentTask: deploymentTask,
            environment: environment,
        });
    }
    if (data.length === 0) {
        return <Callout type="information" title="This release has not been deployed to any environments yet."/>;
    }
    const deploymentDataGroupedByEnvironmentId = groupBy(data, (item) => item.environment.Id);
    return (<LegacyDataTable>
            <LegacyDataTableHeader>
                <LegacyDataTableRow>
                    <LegacyDataTableHeaderColumn>Environment</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Task</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>When</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn />
                </LegacyDataTableRow>
            </LegacyDataTableHeader>
            <LegacyDataTableBody>
                {Object.keys(deploymentDataGroupedByEnvironmentId).map((environmentId) => (<EphemeralEnvironmentDeploymentsRow key={environmentId} project={project} release={release} items={deploymentDataGroupedByEnvironmentId[environmentId]}/>))}
            </LegacyDataTableBody>
        </LegacyDataTable>);
}
